/** 多边形布尔运算封装。
 *
 * 选型：polygon-clipping 0.15（MIT，无原生依赖，浏览器可用）。
 * 已知问题：浮点退化输入（近乎重合的边、零长边）会抛
 * "Unable to complete output ring" / "Unable to find segment in SweepLine tree"。
 *
 * 对策（对应落地方案风险表「多边形布尔在自交/重叠处失败」一行）：
 *   1. 受控整数缩放：先 ×1000 并取整，再进布尔库，结果再除回。
 *   2. 去零长边与重复点。
 *   3. 逐级降精度重试（1000 → 100 → 30 → 10 µm/单位）。
 *   4. 全部失败时降级返回输入，并置 degraded 标志，由上层提示"几何近似"。
 */
import * as pc from 'polygon-clipping';
import type { Polygon, Vec } from '../types';

export type Ring = Vec[];
/** 第 0 项为外环，其余为洞。 */
export type MultiPoly = Ring[][];

type Pair = [number, number];
type Geom = Pair[][][];

const SCALES = [1000, 100, 30, 10] as const;

function scaleRing(ring: Ring, k: number): Pair[] {
  const out: Pair[] = [];
  for (const p of ring) {
    const x = Math.round(p.x * k) / 1;
    const y = Math.round(p.y * k) / 1;
    const last = out[out.length - 1];
    if (last && last[0] === x && last[1] === y) continue;
    out.push([x, y]);
  }
  // 闭合点去重
  if (out.length > 1) {
    const a = out[0];
    const b = out[out.length - 1];
    if (a[0] === b[0] && a[1] === b[1]) out.pop();
  }
  if (out.length && (out[0][0] !== out[out.length - 1][0] || out[0][1] !== out[out.length - 1][1])) {
    out.push([out[0][0], out[0][1]]);
  }
  return out;
}

function unscaleGeom(geom: unknown, k: number): MultiPoly {
  if (!Array.isArray(geom)) return [];
  const out: MultiPoly = [];
  for (const polygonRings of geom as Pair[][][]) {
    if (!Array.isArray(polygonRings) || !polygonRings.length) continue;
    const rings: Ring[] = [];
    for (const pairs of polygonRings) {
      if (!Array.isArray(pairs) || pairs.length < 4) continue;
      const ring: Ring = [];
      for (const p of pairs) {
        if (!Array.isArray(p)) continue;
        ring.push({ x: p[0] / k, y: p[1] / k });
      }
      // 去掉重复的闭合点
      if (ring.length > 1) {
        const a = ring[0];
        const b = ring[ring.length - 1];
        if (Math.abs(a.x - b.x) < 1e-12 && Math.abs(a.y - b.y) < 1e-12) ring.pop();
      }
      if (ring.length >= 3) rings.push(ring);
    }
    if (rings.length) out.push(rings);
  }
  return out;
}

/** 统一转成 polygon-clipping 的 MultiPolygon 形式（已缩放取整）。 */
export function toGeom(poly: Polygon | MultiPoly, k: number): Geom {
  if (!poly.length) return [];
  const head = poly[0] as unknown;
  const isMulti = Array.isArray(head) && head.length > 0 && typeof (head as Ring)[0] === 'object';
  const polys: MultiPoly = isMulti ? (poly as MultiPoly) : [[poly as Polygon]];
  const out: Geom = [];
  for (const rings of polys) {
    const scaled = rings.map((r) => scaleRing(r, k)).filter((r) => r.length >= 4);
    if (scaled.length) out.push(scaled);
  }
  return out;
}

/** 供上层捕获"降级"状态。 */
export let lastDegraded = false;
export function resetDegraded(): void {
  lastDegraded = false;
}

function attempt(
  op: 'union' | 'intersection' | 'difference',
  geoms: Array<Polygon | MultiPoly>,
  primary: Polygon | MultiPoly,
): MultiPoly {
  for (const k of SCALES) {
    try {
      const scaled = geoms.map((g) => toGeom(g, k)).filter((g) => g.length > 0);
      if (!scaled.length) return [];
      let result: unknown;
      if (op === 'union') {
        result = scaled.length === 1 ? scaled[0] : pc.union(scaled[0] as never, ...(scaled.slice(1) as never[]));
      } else if (op === 'intersection') {
        if (scaled.length < 2) return [];
        result = pc.intersection(scaled[0] as never, scaled[1] as never);
      } else {
        result = scaled.length === 1 ? scaled[0] : pc.difference(scaled[0] as never, ...(scaled.slice(1) as never[]));
      }
      const mp = unscaleGeom(result, k);
      return mp;
    } catch {
      continue;
    }
  }
  // 降级：返回主输入本身（不丢数据，但精度/洞结构可能不完整）
  lastDegraded = true;
  const fallback = toGeom(primary, 1);
  return unscaleGeom(fallback, 1);
}

export function unionAll(polys: Array<Polygon | MultiPoly>): MultiPoly {
  const usable = polys.filter((p) => p.length > 0);
  if (!usable.length) return [];
  if (usable.length === 1) {
    const single = usable[0];
    const head = single[0] as unknown;
    const isMulti = Array.isArray(head) && head.length > 0 && typeof (head as Ring)[0] === 'object';
    return isMulti ? (single as MultiPoly) : [[single as Ring]];
  }
  return attempt('union', usable, usable[0]);
}

export function intersectionOf(a: Polygon | MultiPoly, b: Polygon | MultiPoly): MultiPoly {
  if (!a.length || !b.length) return [];
  return attempt('intersection', [a, b], a);
}

export function differenceOf(subject: Polygon | MultiPoly, clips: Array<Polygon | MultiPoly>): MultiPoly {
  if (!subject.length) return [];
  const usable = clips.filter((c) => c.length > 0);
  if (!usable.length) {
    const head = subject[0] as unknown;
    const isMulti = Array.isArray(head) && head.length > 0 && typeof (head as Ring)[0] === 'object';
    return isMulti ? (subject as MultiPoly) : [[subject as Ring]];
  }
  return attempt('difference', [subject, ...usable], subject);
}

/** 总面积（外环减去洞），mm²。 */
export function multiPolyArea(mp: MultiPoly): number {
  let total = 0;
  for (const rings of mp) {
    rings.forEach((ring, i) => {
      let s = 0;
      for (let k = 0; k < ring.length; k++) {
        const p = ring[k];
        const q = ring[(k + 1) % ring.length];
        if (!p || !q) continue;
        s += p.x * q.y - q.x * p.y;
      }
      const a = Math.abs(s / 2);
      total += i === 0 ? a : -a;
    });
  }
  return total;
}

export function multiPolyToPaths(mp: MultiPoly): string {
  let d = '';
  for (const rings of mp) {
    for (const ring of rings) {
      if (ring.length < 3) continue;
      d += `M${ring[0].x} ${ring[0].y}`;
      for (let i = 1; i < ring.length; i++) d += `L${ring[i].x} ${ring[i].y}`;
      d += 'Z';
    }
  }
  return d;
}

export function multiPolyPoints(mp: MultiPoly): Vec[] {
  const out: Vec[] = [];
  for (const rings of mp) for (const ring of rings) out.push(...ring);
  return out;
}

export function cloneMulti(mp: MultiPoly): MultiPoly {
  return mp.map((rings) => rings.map((ring) => ring.map((p) => ({ x: p.x, y: p.y }))));
}

/** 把 MultiPoly 摊平成只含外环的独立多边形列表（忽略洞），用于连通性检查。 */
export function outerRings(mp: MultiPoly): Ring[] {
  return mp.map((rings) => rings[0]).filter((r) => r && r.length >= 3);
}